import { Event_ItemConnectionEdge } from 'types/generated/graphql';
import { EventSliderItemProps } from './EventSliderItem.types';
import { getFormattedDateAndTime } from 'helpers/date';

/* -------------------------------------------------- */
/* Helpers */
/* -------------------------------------------------- */

const getAllDateItems = (body: any[] | null | undefined) =>
  body?.flatMap((slice: any) => slice?.fields || []) || [];

// ✅ Type guard për parent
const hasEventBody = (node: any): node is { body: any[] } => {
  return node?._meta?.type === 'events_uebersicht' && Array.isArray(node.body);
};

/* -------------------------------------------------- */
/* mapEdgeItemToEventBoxItem */
/* -------------------------------------------------- */
export const mapEdgeItemToEventBoxItem = (
  locale: string,
  edgeItem: Event_ItemConnectionEdge
): EventSliderItemProps => {
  const node = edgeItem.node;

  let link = null;

  if (node._linkType && node._meta.uid) {
    link = {
      link_type: node._linkType,
      uid: node._meta.uid,
      lang: locale,
    };
  }

  // 🔑 përdor parent nëse është Events_uebersicht
  const sourceNode = hasEventBody(node.parent) ? node.parent : node;

  const dateItems = getAllDateItems(sourceNode.body);

  const sortedDateItems = [...dateItems].sort(
    (a, b) =>
      new Date(a.event_start).getTime() -
      new Date(b.event_start).getTime()
  );

  const start = sortedDateItems[0]?.event_start;
  const end =
    sortedDateItems[sortedDateItems.length - 1]?.event_end ??
    sortedDateItems[sortedDateItems.length - 1]?.event_start;

  const {
    date: startDate,
    dateStr: startDateStr,
    timeStr: startTimeStr,
  } = getFormattedDateAndTime(start, locale, sortedDateItems.length > 0);

  const {
    dateStr: endDateStr,
    timeStr: endTimeStr,
  } = getFormattedDateAndTime(end, locale, sortedDateItems.length > 0);

  const dateStr =
    startDateStr === endDateStr
      ? startDateStr
      : `${startDateStr} – ${endDateStr}`;

  const timeStr =
    startTimeStr && endTimeStr && startTimeStr !== endTimeStr
      ? `${startTimeStr} – ${endTimeStr}`
      : startTimeStr;

  const locData = sortedDateItems[0]?.default_location;
  const location = locData?.name
    ? (locData.house_nr != null ? `${locData.house_nr} ${locData.name}` : locData.name)
    : (locData?.house_nr != null ? String(locData.house_nr) : null);
  const ort = locData?.ort || null;

  return {
    text: node?.display_name[0]?.text || '',
    image: node?.thumbnail,
    _dateToSort: startDate,
    date: dateStr,
    time: timeStr,
    location,
    ort,
    link,
  };
};

/* -------------------------------------------------- */
/* mapMultipleItems */
/* -------------------------------------------------- */
export const mapMultipleItems = (locale: string, originalItem: any): any => {
  let link: { link_type: string; uid: string; lang: string } | null = null;

  if (originalItem.node._linkType && originalItem.node._meta.uid) {
    link = {
      link_type: originalItem.node._linkType,
      uid: originalItem.node._meta.uid,
      lang: locale,
    };
  }

  if (originalItem?.node?.body?.length > 0) {
return originalItem.node.body.map(() => {
  const sourceNode = hasEventBody(originalItem.node.parent)
    ? originalItem.node.parent
    : originalItem.node;

  const dateItems = getAllDateItems(sourceNode.body);


      const sortedDateItems = [...dateItems].sort(
        (a, b) =>
          new Date(a.event_start).getTime() -
          new Date(b.event_start).getTime()
      );

      const start = sortedDateItems[0]?.event_start;
      const end =
        sortedDateItems[sortedDateItems.length - 1]?.event_end ??
        sortedDateItems[sortedDateItems.length - 1]?.event_start;

      const {
        date,
        dateStr: startDateStr,
        timeStr: startTimeStr,
      } = getFormattedDateAndTime(start, locale, sortedDateItems.length > 0);

      const {
        dateStr: endDateStr,
        timeStr: endTimeStr,
      } = getFormattedDateAndTime(end, locale, sortedDateItems.length > 0);

      const dateStr =
        startDateStr === endDateStr
          ? startDateStr
          : `${startDateStr} – ${endDateStr}`;

      const timeStr =
        startTimeStr && endTimeStr && startTimeStr !== endTimeStr
          ? `${startTimeStr} – ${endTimeStr}`
          : startTimeStr;

      const locData = sortedDateItems[0]?.default_location;
      const location = locData?.name
        ? (locData.house_nr != null ? `${locData.house_nr} ${locData.name}` : locData.name)
        : (locData?.house_nr != null ? String(locData.house_nr) : null);
      const ort = locData?.ort || null;

      return {
        text: originalItem.node.display_name[0]?.text || '',
        image: originalItem.node.thumbnail,
        _dateToSort: date,
        date: dateStr,
        time: timeStr,
        location,
        ort,
        link,
      };
    });
  }
};

/* -------------------------------------------------- */
/* mapTagesplanEdgeItemToEventBoxItem */
/* -------------------------------------------------- */
export const mapTagesplanEdgeItemToEventBoxItem = (
  locale: string,
  edgeItem: Event_ItemConnectionEdge
): EventSliderItemProps => {
  const node = edgeItem.node;

  let link = null;

  if (node._linkType && node._meta.uid) {
    link = {
      link_type: node._linkType,
      uid: node._meta.uid,
      lang: locale,
    };
  }

  // 🔑 edhe këtu parent nëse ekziston
  const sourceNode = hasEventBody(node.parent) ? node.parent : node;

  const dateItems = getAllDateItems(sourceNode.body);

  const sortedDateItems = [...dateItems].sort(
    (a, b) =>
      new Date(a.event_start).getTime() -
      new Date(b.event_start).getTime()
  );

  const start = sortedDateItems[0]?.event_start;
  const end =
    sortedDateItems[sortedDateItems.length - 1]?.event_end ??
    sortedDateItems[sortedDateItems.length - 1]?.event_start;

  const {
    dateStr: startDateStr,
    timeStr: startTimeStr,
  } = getFormattedDateAndTime(start, locale, sortedDateItems.length > 0);

  const {
    dateStr: endDateStr,
    timeStr: endTimeStr,
  } = getFormattedDateAndTime(end, locale, sortedDateItems.length > 0);

  const dateStr =
    startDateStr === endDateStr
      ? startDateStr
      : `${startDateStr} – ${endDateStr}`;

  const timeStr =
    startTimeStr && endTimeStr && startTimeStr !== endTimeStr
      ? `${startTimeStr} – ${endTimeStr}`
      : startTimeStr;

  const locData = sortedDateItems[0]?.default_location;
  const location = locData?.name
    ? (locData.house_nr != null ? `${locData.house_nr} ${locData.name}` : locData.name)
    : (locData?.house_nr != null ? String(locData.house_nr) : null);
  const ort = locData?.ort || null;

  return {
    text: node?.display_name[0]?.text || '',
    image: node?.thumbnail,
    date: dateStr,
    time: timeStr,
    location,
    ort,
    link,
  };
};
